import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { MobileNavDetails } from "../../../layout/MobileNavbar";
import Container from "../../../layout/Container";
import Navbar from "../../../layout/Navbar";
import {
	BackTracker,
	SavingsButtonNavigation,
	UrlTracker,
} from "../../../layout/CustomComponents";
import { RegularSavingsCard } from "../../leasing/regularsavings/RegularSavingsView";
import SingleSavingsEmpty from "../../../components/regularsavingscomp/SingleSavingsEmpty";
import { Preloader } from "../../../components/globalStyled";
import {
	clearRegularSavingsCreatedSuccess,
	openMoveFundsFromRegSavingsModal,
} from "../../../redux/lease/leaseActions";
import {
	fetchSingleRegularSavings,
	updateSingleRegularSavings,
} from "../../../redux/travels/travelServices";
import ModalContainer from "../../../components/ModalContainer";
import AddFundsToRegularSavingsFlow from "../../../components/regularsavingscomp/AddFundsToRegularSavingsFlow";
import {
	clearUpdateRegularSavingsSuccess,
	closeRegularSavingsAddFundsModal,
	openRegularSavingsAddFundsModal,
} from "../../../redux/travels/travelActions";
import Parse from "../../../api/ApiConfig";
import { clearMoveFundsSuccess } from "../../../redux/payments/paymentActions";
import { walletType } from "../../../common/utils";
import { moveUserFunds } from "../../../redux/payments/paymentServices";
import { logoutUser } from "../../../redux/authRedux/authServices";
import MoveFundsFromRegularSavings from "../../../components/regularsavingscomp/MoveFundsFromRegularSavings";

const SingleTravelAdvanceSavings = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const params = useParams();
	const savingsSource = "travel";

	const [activeModal, setActiveModal] = useState(1);
	const [amountToSave, setAmountToSave] = useState("");
	const [amountToMove, setAmountToMove] = useState("");
	const [activeMoveFundsModal, setActiveMoveFundsModal] = useState(false);

	const currentSessionToken = Parse.User.current()?.getSessionToken();
	const user = Parse.User.current();

	const {
		fetchSingleRegSavingsLoading,
		fetchSingleRegSavingsSuccess,
		toggleRegularSavingsAddFundsModal,
		updateRegularSavingsSuccess,
		toggleMoveFundsFromRegSavingsModal,
	} = useSelector((state) => state.travel);

	const { customerWallet, moveFundsSuccess } = useSelector(
		(state) => state.payment
	);

	const mainWallet = customerWallet?.find(
		(wallet) => wallet?.product === walletType.main
	);

	const isPlanExpired =
		Date.now() > new Date(`${fetchSingleRegSavingsSuccess?.maturityDate}`);

	const onProceedToDeductWallet = () => {
		const amountPayable = Number(amountToSave?.replaceAll(",", ""));

		const details = {
			amount: amountPayable,
			source: "main",
			destination: walletType.regularSavings,
			message: "travel-savings-funds-moved",
		};

		if (mainWallet?.availableBalance < amountPayable) {
			toast.error("Insuffient balance. Please fund your wallet!");
			return;
		}

		if (user.id) {
			dispatch(moveUserFunds(currentSessionToken, details));
		} else {
			dispatch(logoutUser());
		}
	};

	// open add funds modal
	const onOpenAddFundsModal = () => {
		if (isPlanExpired) {
			toast.error("You cannot add funds to expired plan.");
		} else {
			dispatch(openRegularSavingsAddFundsModal());
			setActiveModal(1);
		}
	};

	//open move funds modal
	const onOpenMoveFundsModal = () => {
		if (
			!fetchSingleRegSavingsSuccess?.amountSaved ||
			Number(fetchSingleRegSavingsSuccess?.amountSaved?.replaceAll(",", "")) < 1
		) {
			toast.error("You do not have any funds available.");
			return;
		}
		dispatch(openMoveFundsFromRegSavingsModal());
		setActiveMoveFundsModal(1);
	};

	useEffect(() => {
		dispatch(clearRegularSavingsCreatedSuccess());
		dispatch(fetchSingleRegularSavings(params.id, savingsSource));
	}, [params]);

	useEffect(() => {
		if (moveFundsSuccess === "travel-savings-funds-moved") {
			const oldAmount = fetchSingleRegSavingsSuccess?.amountSaved
				? Number(fetchSingleRegSavingsSuccess?.amountSaved?.replaceAll(",", ""))
				: 0;
			const updatedAmount =
				oldAmount + Number(amountToSave?.replaceAll(",", ""));

			dispatch(
				updateSingleRegularSavings(
					params.id,
					{ amountSaved: updatedAmount?.toString() },
					{
						amount: amountToSave,
						planName: fetchSingleRegSavingsSuccess?.purpose || "General",
					}
				)
			);
		} else {
			return;
		}
	}, [moveFundsSuccess]);

	useEffect(() => {
		if (updateRegularSavingsSuccess) {
			dispatch(clearMoveFundsSuccess());
			setActiveModal(4);
		} else {
			return;
		}
	}, [updateRegularSavingsSuccess]);

	const clearUpdateSavingsSuccess = () => {
		dispatch(clearMoveFundsSuccess());
		dispatch(closeRegularSavingsAddFundsModal());
		dispatch(clearUpdateRegularSavingsSuccess());
	};

	return (
		<>
			{toggleRegularSavingsAddFundsModal && (
				<ModalContainer modal={toggleRegularSavingsAddFundsModal}>
					<AddFundsToRegularSavingsFlow
						activeModal={activeModal}
						setActiveModal={setActiveModal}
						onDeductWallet={onProceedToDeductWallet}
						amountToSave={amountToSave}
						setAmountToSave={setAmountToSave}
						onDeductWalletSuccess={clearUpdateSavingsSuccess}
					/>
				</ModalContainer>
			)}
			{toggleMoveFundsFromRegSavingsModal && (
				<ModalContainer modal={toggleMoveFundsFromRegSavingsModal}>
					<MoveFundsFromRegularSavings
						activeMoveFundsModal={activeMoveFundsModal}
						setActiveMoveFundsModal={setActiveMoveFundsModal}
						amountToMove={amountToMove}
						setAmountToMove={setAmountToMove}
						amountSaved={fetchSingleRegSavingsSuccess?.amountSaved}
					/>
				</ModalContainer>
			)}
			<MobileNavDetails text="travel advance" />
			<Helmet>
				<title>Fortcore - Regular Savings</title>
			</Helmet>
			<Container>
				<>
					<Navbar text="travel advance" />
					<div className="sm:pt-9 md:pt-10 "></div>
					<BackTracker text="regular savings" onNavigate={() => navigate(-1)} />
					<div className="flex gap-1.5">
						<UrlTracker
							text="travel advance"
							onNavigate={() => navigate("/travels")}
						/>
						<UrlTracker
							text="regular savings"
							onNavigate={() => navigate("/travels/regular-savings/view")}
						/>
						<UrlTracker text={params?.id} isCurrentUrl />
					</div>
					<div className="sm:mt-6"></div>
					{fetchSingleRegSavingsLoading ? (
						<div className="h-[350px] sm:h-[400px] grid place-items-center w-full">
							<Preloader />
						</div>
					) : fetchSingleRegSavingsSuccess ? (
						<div className="space-y-5 sm:space-y-6 md:space-y-7 mb-8">
							<RegularSavingsCard
								data={fetchSingleRegSavingsSuccess}
								showMaturityDate
							/>
							<div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
								<SavingsButtonNavigation
									text={
										Number(
											fetchSingleRegSavingsSuccess?.amountSaved?.replaceAll(
												",",
												""
											)
										) > 0
											? "add more funds"
											: "start saving"
									}
									onClick={onOpenAddFundsModal}
								/>
								<SavingsButtonNavigation
									text="move funds"
									onClick={onOpenMoveFundsModal}
								/>
								<SavingsButtonNavigation text="history" />
							</div>
						</div>
					) : (
						<SingleSavingsEmpty query={params?.id} savingUrl={savingsSource} />
					)}
				</>
			</Container>
		</>
	);
};

export default SingleTravelAdvanceSavings;
